import type { CheckboxProps } from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';

import type { ButtonProps } from '../../../Button/Button.tsx';
import { CheckboxField } from '../../../CheckboxField/CheckboxField.tsx';

import {
  StyledActionsGrid,
  StyledButton,
  StyledCheckboxGrid,
  StyledContainerGrid,
} from './ListHeaderActions.styled.ts';

export type ListHeaderActionButton = {
  color?: ButtonProps['color'];
  text: string;
  onClick: ButtonProps['onClick'];
};

export type ListHeaderActionsProps = {
  buttons?: ListHeaderActionButton[];
  checkboxDataTest?: string;
  isDisabled?: boolean;
  itemsCount: number;
  itemsText?: string;
  onCheckboxChange?: CheckboxProps['onChange'];
  selectedItemsCount: number;
};

export const ListHeaderActions = ({
  buttons,
  checkboxDataTest,
  isDisabled = false,
  itemsCount,
  itemsText,
  onCheckboxChange,
  selectedItemsCount,
}: ListHeaderActionsProps) => {
  const { t } = useTranslation();

  const hasCheckbox = !!onCheckboxChange;
  const selectedItemsCountText = `${selectedItemsCount}/${itemsCount} ${t('selected')}`;

  return (
    <StyledContainerGrid container>
      {hasCheckbox && (
        <StyledCheckboxGrid size={1}>
          <CheckboxField
            data-test={checkboxDataTest}
            checked={selectedItemsCount > 0}
            disabled={isDisabled || itemsCount === 0}
            indeterminate={selectedItemsCount > 0 && selectedItemsCount !== itemsCount}
            onChange={onCheckboxChange}
          />
        </StyledCheckboxGrid>
      )}
      <StyledActionsGrid size={hasCheckbox ? 11 : 12}>
        <Typography data-test="items-count" color="textSecondary" variant="body1">
          {itemsText ?? selectedItemsCountText}
        </Typography>

        <Box>
          {buttons?.map(({ color, onClick, text }, index) => (
            <StyledButton
              color={color}
              data-test={`${text.toLowerCase().replace(/\s+/, '-')}-button`}
              disabled={isDisabled}
              key={`${index}-${text}`}
              onClick={onClick}
              variant="text"
            >
              {text}
            </StyledButton>
          ))}
        </Box>
      </StyledActionsGrid>
    </StyledContainerGrid>
  );
};
